// DepositFlowPage — Multi-step deposit flow
// Steps: 1) Method → 2) Amount → 3) Confirm → 4) Processing
// Supports: ACH, Wire, Card, Crypto, Apple Pay

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerLogo } from '../../components/Brand';

const METHODS = [
  { key: 'apple-pay',   label: 'Apple Pay',        sub: 'Instant',         fee: 'Free',  min: 10,  max: 10000, icon: '', accent: '#1C1C1E' },
  { key: 'card',        label: 'Debit / Credit',   sub: 'Instant',         fee: '1.5%',  min: 10,  max: 5000,  icon: '💳', accent: '#007AFF' },
  { key: 'ach',         label: 'Bank transfer',    sub: '2-3 business days', fee: 'Free',  min: 100, max: 50000, icon: '🏦', accent: '#34C759' },
  { key: 'wire',        label: 'Wire transfer',    sub: '1-2 business days', fee: '$25',   min: 1000, max: 250000, icon: '🌐', accent: '#FF9500' },
  { key: 'crypto',      label: 'Crypto (USDC)',    sub: '~5 min',          fee: 'Network', min: 25,  max: 100000, icon: '🪙', accent: '#5856D6' },
] as const;
type MethodKey = typeof METHODS[number]['key'];

export function DepositFlowPage() {
  const nav = useNavigate();
  const [method, setMethod] = useState<MethodKey | null>(null);
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<'method' | 'amount' | 'confirm' | 'processing' | 'done'>('method');
  const m = METHODS.find((x) => x.key === method);

  function start() {
    if (step === 'method' && method) setStep('amount');
    else if (step === 'amount' && Number(amount) > 0) setStep('confirm');
    else if (step === 'confirm') {
      setStep('processing');
      setTimeout(() => setStep('done'), 2200);
    }
    else if (step === 'done') nav('/portal/wallet');
  }

  function back() {
    if (step === 'amount') setStep('method');
    else if (step === 'confirm') setStep('amount');
    else nav(-1);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 flex flex-col">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={back} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Add funds</div>
        <button onClick={() => nav(-1)} data-testid="cancel" className="px-3 text-[15px] text-tf-blue">Cancel</button>
      </div>

      <div className="flex-1 overflow-y-auto pb-32">
        {step === 'method' && <MethodStep method={method} setMethod={setMethod} />}
        {step === 'amount' && m && <AmountStep method={m} amount={amount} setAmount={setAmount} />}
        {step === 'confirm' && m && <ConfirmStep method={m} amount={amount} />}
        {step === 'processing' && <ProcessingStep method={m!} amount={amount} />}
        {step === 'done' && m && <DoneStep method={m} amount={amount} onContinue={start} />}
      </div>

      {step !== 'done' && step !== 'processing' && (
        <div className="fixed inset-x-0 bottom-0 z-10 bg-white/80 backdrop-blur-xl border-t border-tf-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <button
            onClick={start}
            disabled={(step === 'method' && !method) || (step === 'amount' && (!amount || Number(amount) < (m?.min || 0)))}
            data-testid="deposit-next"
            className="flex h-12 w-full items-center justify-center btn-spring disabled:opacity-40"
          >
            {step === 'method' ? 'Continue' : step === 'amount' ? 'Review' : 'Confirm deposit'}
          </button>
        </div>
      )}
    </div>
  );
}

function MethodStep({ method, setMethod }: { method: MethodKey | null; setMethod: (m: MethodKey) => void }) {
  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Choose method</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">All deposits are processed in USD via our partner <PartnerLogo name="stripe" height={14} className="inline-block align-middle" /></p>
      <div className="ios26-list bg-white">
        {METHODS.map((m, i) => (
          <button
            key={m.key}
            onClick={() => setMethod(m.key)}
            data-testid={`method-${m.key}`}
            className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < METHODS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            {m.key === 'apple-pay' ? (
              <div className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-black text-white text-[20px]"></div>
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: m.accent + '18' }}>{m.icon}</div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[14px] font-semibold text-tf-text-1">{m.label}</div>
              <div className="text-[11px] text-tf-text-3">{m.sub} · Fee {m.fee}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-tf-text-3">${m.min.toLocaleString()} - ${m.max.toLocaleString()}</div>
              {method === m.key && (
                <svg viewBox="0 0 24 24" className="ml-auto h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AmountStep({ method, amount, setAmount }: { method: typeof METHODS[number]; amount: string; setAmount: (v: string) => void }) {
  const num = Number(amount) || 0;
  const fee = method.fee === 'Free' ? 0 : method.fee === 'Network' ? 2 : method.fee === '$25' ? 25 : num * 0.015;
  const presets = method.key === 'apple-pay' || method.key === 'card' ? [50, 100, 250, 500] : method.key === 'wire' ? [5000, 10000, 25000, 50000] : [100, 500, 1000, 5000];

  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Amount</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Min ${method.min.toLocaleString()} · Max ${method.max.toLocaleString()}</p>

      <div className="rounded-[20px] bg-white p-6 text-center">
        <div className="text-[12px] uppercase tracking-wider text-tf-text-3">You're adding</div>
        <div className="mt-1 flex items-center justify-center">
          <span className="text-[32px] font-bold text-tf-text-1">$</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            data-testid="deposit-amount"
            className="ml-1 w-48 bg-transparent text-[44px] font-bold text-tf-text-1 tabular-nums focus:outline-none text-center"
          />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2" data-testid="deposit-presets">
        {presets.map((p) => (
          <button key={p} onClick={() => setAmount(String(p))} className="h-10 rounded-full bg-white border border-tf-border text-[14px] font-semibold text-tf-text-1 active:opacity-40" data-testid={`preset-${p}`}>
            ${p.toLocaleString()}
          </button>
        ))}
      </div>

      <div className="mt-4 ios26-list bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-tf-border">
          <span className="text-[13px] text-tf-text-3">Fee</span>
          <span className="text-[13px] font-semibold text-tf-text-1">${fee.toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-[13px] text-tf-text-3">Total</span>
          <span className="text-[15px] font-bold tabular-nums text-tf-text-1">${(num + fee).toFixed(2)}</span>
        </div>
      </div>

      {num > 0 && num < method.min && (
        <p className="mt-3 text-center text-[12px] text-tf-loss">Minimum is ${method.min.toLocaleString()}</p>
      )}
    </div>
  );
}

function ConfirmStep({ method, amount }: { method: typeof METHODS[number]; amount: string }) {
  const num = Number(amount);
  const fee = method.fee === 'Free' ? 0 : method.fee === 'Network' ? 2 : method.fee === '$25' ? 25 : num * 0.015;
  return (
    <div className="px-4 pt-4">
      <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Confirm</h2>
      <p className="mt-1 mb-4 text-[14px] text-tf-text-3">Review your deposit details before submitting.</p>

      <div className="rounded-[20px] bg-white p-5 text-center">
        <div className="text-[12px] uppercase tracking-wider text-tf-text-3">Adding to wallet</div>
        <div className="mt-1 text-[44px] font-bold text-tf-text-1 tabular-nums">${num.toFixed(2)}</div>
        <div className="mt-1 text-[13px] text-tf-text-3">via {method.label} · {method.sub}</div>
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Breakdown</h3>
      <div className="ios26-list bg-white">
        <Row label="Amount" value={`$${num.toFixed(2)}`} />
        <Row label={`${method.label} fee`} value={`$${fee.toFixed(2)}`} />
        <Row label="Total" value={`$${(num + fee).toFixed(2)}`} bold />
        <Row label="Arrives" value={method.sub} />
      </div>

      <div className="mt-4 rounded-[14px] border border-[#34C759]/30 bg-[#34C759]/[0.04] p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">🔒</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-profit">Secured by Stripe</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">Your card details are tokenized and never stored on our servers.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProcessingStep({ method, amount }: { method: typeof METHODS[number]; amount: string }) {
  return (
    <div className="flex flex-col items-center justify-center pt-24 px-4 text-center">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 100 100" className="h-20 w-20 animate-spin" style={{ animationDuration: '1.2s' }}>
          <circle cx="50" cy="50" r="42" stroke="#F2F2F7" strokeWidth="6" fill="none" />
          <circle cx="50" cy="50" r="42" stroke="#007AFF" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="80 184" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[28px]">⏳</div>
      </div>
      <h2 className="mt-6 text-[20px] font-bold tracking-tight text-tf-text-1">Processing your deposit</h2>
      <p className="mt-1 text-[13px] text-tf-text-3">${Number(amount).toFixed(2)} via {method.label}</p>
      <p className="mt-0.5 text-[11px] text-tf-text-3">Please don't close this page</p>
    </div>
  );
}

function DoneStep({ method, amount, onContinue }: { method: typeof METHODS[number]; amount: string; onContinue: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center pt-16 px-4 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#34C759] text-white">
        <svg viewBox="0 0 24 24" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
      <h2 className="mt-6 text-[24px] font-bold tracking-tight text-tf-text-1">Deposit confirmed</h2>
      <p className="mt-1 text-[14px] text-tf-text-3">${Number(amount).toFixed(2)} via {method.label}</p>
      <p className="mt-0.5 text-[12px] text-tf-text-3">Arrives in your wallet {method.sub}</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#34C759]/15 px-3 py-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
        <span className="text-[12px] font-semibold text-tf-profit">TX-7C29A84F</span>
      </div>
      <button onClick={onContinue} data-testid="deposit-done" className="mt-8 h-12 w-full max-w-xs btn-spring">View wallet</button>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-tf-border last:border-b-0 px-4 py-2.5">
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className={['tabular-nums', bold ? 'text-[15px] font-bold text-tf-text-1' : 'text-[13px] font-medium text-tf-text-1'].join(' ')}>{value}</span>
    </div>
  );
}
